import { cyboRosterMirror } from "./core/cybo-roster-provisional.svelte.js";

// Single source of truth for an agent's display name.
//
// HISTORY (why this exists): the "cybo agent shows its PROVIDER instead of its
// name" bug regressed repeatedly (0b951ac0, c8418f83, 0dcc1830, …) because every
// surface — AgentsPane, ChannelSidebar, HomePane, the agent page, ProfilePanel,
// DaemonDetail — kept its OWN copy of the fallback chain (and its own
// PROVIDER_LABELS), and each new/redesigned surface silently dropped a step.
// The June-1 fix denormalized `cyboName` into the daemon's list_agents response
// (and the relay aggregates rows verbatim, so it survives cloud mode); what
// kept breaking was the client-side resolution. So: ONE resolver, used by every
// surface. agent-display.test.ts has a guard that fails the build if a new
// local resolver or PROVIDER_LABELS copy appears.
//
// Priority order:
//   1. cyboName  — server-denormalized by the daemon (covers DB + local cybos).
//   2. cyboId looked up in the caller's cybo list — fallback for older daemons.
//   2b. the cybo is DELETED (see isDeletedCyboBinding) — an explicit label, never
//       step 3: the provider name is not this agent's identity, and printing it
//       for a binding whose cybo is gone INVENTS one ("Claude").
//   3. PROVIDER_LABELS pretty name, else the raw provider id.
//   4. agentId prefix, else "Agent".

export const PROVIDER_LABELS: Record<string, string> = {
  claude: "Claude",
  codex: "Codex",
  opencode: "OpenCode",
  pi: "Agent",
  copilot: "Copilot",
  qwen: "Qwen",
  gemini: "Gemini",
  openai: "OpenAI",
  deepseek: "DeepSeek",
};

export interface AgentNameSource {
  cyboName?: string | null;
  cyboId?: string | null;
  provider?: string | null;
  agentId?: string | null;
}

// What a surface calls an agent whose cybo has been deleted and whose last-known
// name we do not hold. Deliberately NOT a provider label: see step 2b above.
export const DELETED_AGENT_NAME = "Deleted agent";

/**
 * Does this session binding point at a cybo that no longer exists?
 *
 * THE BUG THIS ANSWERS. Deleting a cybo prunes `cyboState.list`, but its agent
 * BINDINGS live in `workspaceState.agents` — and every DM surface (the sidebar
 * row, /agent/<id>, ProfilePanel) resolves identity, the status dot and the
 * composer from a binding. So a deleted agent kept rendering as a live one: an
 * AGENT badge, a green dot (honest about its DAEMON, mute about the agent), and
 * a name invented from its provider. Pruning the broadcast handler fixes the
 * client that RECEIVED it; a client that was offline, restarted, or is another
 * device still holds the row, so the render path has to be able to tell.
 *
 * FAILS OPEN, twice, because a false positive would hide a live agent:
 *   • no `cyboId` — a plain provider/CLI session is not a cybo binding at all;
 *   • an EMPTY roster — indistinguishable from "not loaded yet" / "the fetch
 *     failed", both of which are frames every client passes through on a cold
 *     open. A workspace whose cybos really are all deleted reads as unknown
 *     rather than dead; that is the deliberate cost of never lying the other way.
 * ponytail: `cybos.length > 0` is the loaded-ness proxy. If CyboState ever grows
 * an explicit "fetched at least once" flag, take that instead.
 */
export function isDeletedCyboBinding(
  agent: { cyboId?: string | null } | null | undefined,
  cybos: ReadonlyArray<{ id: string }> | null | undefined,
): boolean {
  if (!agent?.cyboId || !cybos || cybos.length === 0 || cyboRosterMirror.provisional) return false;
  return !cybos.some((c) => c.id === agent.cyboId);
}

export function agentDisplayName(
  agent: AgentNameSource | null | undefined,
  cybos?: ReadonlyArray<{ id: string; name: string }>,
): string {
  // Defensive: rows arrive over the wire; a missing agent must never throw.
  if (!agent) return "Agent";
  // A denormalized name is kept even for a deleted cybo — the LAST KNOWN name is
  // a truer answer than a generic label, and the surfaces mark the row dead by
  // its dot/badge/composer, not by erasing who it was.
  const named = cyboNameOf(agent, cybos);
  if (named) return named;
  if (isDeletedCyboBinding(agent, cybos)) return DELETED_AGENT_NAME;
  if (agent.provider) return PROVIDER_LABELS[agent.provider] ?? agent.provider;
  return agent.agentId ? agent.agentId.slice(0, 10) : "Agent";
}

// Steps 1-2 only (cyboName, then roster by cyboId), undefined when neither
// resolves. For rows that are NOT agents (schedules) or that keep their own
// tail, so the caller supplies what to show instead: `cyboNameOf(s) ?? s.cyboId`.
export function cyboNameOf(
  row: { cyboName?: string | null; cyboId?: string | null } | null | undefined,
  cybos?: ReadonlyArray<{ id: string; name: string }>,
): string | undefined {
  // A blank name ("", "  ") is no name: the caller's fallback shows instead.
  const own = row?.cyboName?.trim();
  if (own) return own;
  if (!row?.cyboId || !cybos) return undefined;
  return cybos.find((c) => c.id === row.cyboId)?.name.trim() || undefined;
}

// ─── Provider brand colors ────────────────────────────────────────────────
// Avatar background colors for known provider CLIs, so e.g. Claude reads as its
// signature orange on every surface (roster, home, agent header). Unknown
// providers return undefined — callers fall back to a hashed/neutral color.
export const PROVIDER_BRAND_COLORS: Record<string, string> = {
  claude: "#D97757", // Anthropic coral/orange
  codex: "#10A37F", // OpenAI green
  gemini: "#4285F4", // Google blue
};

export function providerBrandColor(provider: string | null | undefined): string | undefined {
  return provider ? PROVIDER_BRAND_COLORS[provider.toLowerCase()] : undefined;
}

// Reverse lookup: the provider id for a pretty label ("Claude" -> "claude").
// Used to heal a message whose fromName is a provider label stamped by the
// daemon (a generic non-cybo session's post) after its agent session is gone
// from the roster - the label alone still resolves to the provider glyph.
export function providerIdForLabel(name: string | null | undefined): string | null {
  if (!name) return null;
  for (const [id, label] of Object.entries(PROVIDER_LABELS)) {
    if (label === name) return id;
  }
  return null;
}

// The provider in a relay session stamp: "Codex" or "Ana's Codex" (server genericName).
export function sessionStampProvider(name: string | null | undefined): string | null {
  const exact = providerIdForLabel(name);
  if (exact || !name) return exact;
  const at = name.lastIndexOf("'s ");
  return at > 0 ? providerIdForLabel(name.slice(at + 3)) : null; // null: not a session stamp
}

// False for a session that left the roster: its profile would show a raw id.
export function agentAuthorHasProfile(
  fromId: string,
  cybos: ReadonlyArray<{ id: string; slug?: string }> | undefined,
  agents: ReadonlyArray<{ agentId: string; cyboId?: string | null }> | undefined,
): boolean {
  if (fromId.startsWith("provider:") || (!cybos?.length && !agents?.length)) return true;
  return (
    !!cybos?.some((c) => c.id === fromId || c.slug === fromId) ||
    !!agents?.some((a) => a.agentId === fromId || a.cyboId === fromId)
  );
}

/** Mention-style handle derived from the display name ("Apex" -> "apex"). */
export function agentHandle(
  agent: AgentNameSource | null | undefined,
  cybos?: ReadonlyArray<{ id: string; name: string }>,
): string {
  const handle = agentDisplayName(agent, cybos)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (handle) return handle;
  // A name made entirely of non-latin/emoji characters strips to "" — fall
  // back to the agent id so the mention handle is never empty.
  const id = (agent?.agentId ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return id ? id.slice(0, 10) : "agent";
}
